import { z } from "zod";

const slugSchema = z
  .string()
  .transform((val) => (val.trim() === "" ? undefined : val.trim()))
  .pipe(
    z
      .string()
      .min(2, "Slug must be at least 2 characters")
      .max(200, "Slug must be at most 200 characters")
      .regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers, and hyphens only")
      .optional(),
  );

const membershipTypeSchema = z.enum(["open", "invite_only", "application"]);

export const createNetworkSchema = z.object({
  name: z.string().min(2, "Network name must be at least 2 characters").max(200),
  slug: slugSchema,
  description: z.string().max(2000).optional(),
  logoUrl: z.string().url("Must be a valid URL").max(500).optional().or(z.literal("")),
  membershipType: membershipTypeSchema.default("invite_only"),
});

export const updateNetworkSchema = z.object({
  name: z.string().min(2, "Network name must be at least 2 characters").max(200).optional(),
  slug: slugSchema,
  description: z.string().max(2000).optional(),
  logoUrl: z.string().url("Must be a valid URL").max(500).optional().or(z.literal("")),
  membershipType: membershipTypeSchema.optional(),
});

export type CreateNetworkFormData = z.infer<typeof createNetworkSchema>;
export type UpdateNetworkFormData = z.infer<typeof updateNetworkSchema>;

// --- Tier Schemas ---

export const createTierSchema = z.object({
  name: z.string().min(1, "Tier name is required").max(200),
  description: z.string().max(2000).optional().or(z.literal("")),
  monthlyPriceCents: z.coerce.number().int().min(0, "Price cannot be negative"),
  annualPriceCents: z.coerce.number().int().min(0, "Price cannot be negative"),
});

export const updateTierSchema = z.object({
  name: z.string().min(1, "Tier name is required").max(200).optional(),
  description: z.string().max(2000).optional().or(z.literal("")),
  monthlyPriceCents: z.coerce.number().int().min(0, "Price cannot be negative").optional(),
  annualPriceCents: z.coerce.number().int().min(0, "Price cannot be negative").optional(),
});

export type CreateTierFormData = z.infer<typeof createTierSchema>;
export type UpdateTierFormData = z.infer<typeof updateTierSchema>;

// --- Membership Schemas ---

export const invitePodcastSchema = z.object({
  podcastId: z.string().min(1, "Podcast is required"),
  tierId: z.string().optional().or(z.literal("")),
  revenueSplitPercent: z.coerce
    .number()
    .min(0, "Split cannot be negative")
    .max(100, "Split cannot exceed 100%")
    .optional(),
});

export const updateMembershipSchema = z.object({
  tierId: z.string().optional().or(z.literal("")),
  revenueSplitPercent: z.coerce
    .number()
    .min(0, "Split cannot be negative")
    .max(100, "Split cannot exceed 100%"),
});

export type InvitePodcastFormData = z.infer<typeof invitePodcastSchema>;
export type UpdateMembershipFormData = z.infer<typeof updateMembershipSchema>;

export const MEMBERSHIP_TYPES = [
  {
    value: "open" as const,
    label: "Open",
    description: "Any podcast can join freely without approval",
  },
  {
    value: "invite_only" as const,
    label: "Invite Only",
    description: "Podcasts can only join when invited by the network owner",
  },
  {
    value: "application" as const,
    label: "Application",
    description: "Podcasts submit an application that must be approved",
  },
];
